import { HttpClient } from '@angular/common/http';
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, shareReplay, Subject, tap } from 'rxjs';
import { GATEWAY_URL } from './api.config';
import { RefreshableSession, SIGNED_IN_ELSEWHERE_REASON, SIGNED_IN_ELSEWHERE_STATE, withSession } from './auth-session';
import { browserId } from './browser-id';
import { DEFAULT_SESSION_TIMEOUT_MINUTES, IdleTimer, SessionSettings } from './idle-timer';
import { PublicEndUser, SessionConflict, TokenPair, TwoFactorSetup } from './models';
import { SESSION_SOCKET_FACTORY, SessionSocket } from './session-socket';

/**
 * The end-user (consumer / patient) session — its OWN storage keys, separate
 * from the admin and invited sessions, so all three can coexist in one browser.
 * Authenticated calls declare this session (`withSession('end_user')`); the
 * shared interceptor attaches the end-user token, refreshes it when it expires,
 * and ends the session when the refresh token is dead. Inactivity ends it too,
 * after the user's session timeout — the same behaviour as the admin portal.
 */
const TOKENS_KEY = 'auth_m_enduser_tokens';
const USER_KEY = 'auth_m_enduser_user';

export interface EndUserSignInResponse {
  status: '2fa_required' | 'ok' | 'session_conflict';
  method?: string;
  challenge_token?: string;
  sms_available?: boolean;
  authenticator_available?: boolean;
  /** Where an email code went, hidden for display: signing in with a username, the app never saw the address. */
  email_masked?: string;
  user?: PublicEndUser;
  tokens?: TokenPair;
  session_conflict?: SessionConflict;
}

/** 2FA verify and signin/confirm: signed in, or stopped at a session conflict. */
export interface EndUserAuthResponse {
  status: 'ok' | 'session_conflict';
  user?: PublicEndUser;
  tokens?: TokenPair;
  session_conflict?: SessionConflict;
}

@Injectable({ providedIn: 'root' })
export class EndUserAuthService implements RefreshableSession {
  logoutEvent: Subject<any> = new Subject<any>();

  readonly user = signal<PublicEndUser | null>(readJson<PublicEndUser>(USER_KEY));

  private readonly idle: IdleTimer;
  private readonly socket: SessionSocket;
  private refreshInFlight$: Observable<TokenPair> | null = null;

  constructor(private http: HttpClient, private router: Router, zone: NgZone) {
    // Inactivity ends the session and returns the user to the login page —
    // the same behaviour the admin portal has.
    this.idle = new IdleTimer(zone, () => this.timedOut());
    // A sign-in from another browser or device ends the session at once.
    this.socket = new SessionSocket(zone, inject(SESSION_SOCKET_FACTORY),
      () => this.tokens?.access_token, () => this.refresh(), () => this.sessionRevoked());
    // A session restored from storage (a page reload) is watched from the
    // start. Deferred because the settings request passes through the
    // interceptor, which injects this service — construction must finish first.
    if (this.isLoggedIn) {
      queueMicrotask(() => {
        this.startIdleWatch();
        this.socket.connect();
      });
    }
  }

  private base = `${GATEWAY_URL}/auth/end-users`;

  // ── self sign-up ────────────────────────────────────────────────────────
  // The username is what this account will sign in with (see core/username.ts).
  register(
    email: string,
    username: string,
    password: string,
    firstName: string,
    lastName: string,
  ): Observable<PublicEndUser> {
    return this.http.post<PublicEndUser>(`${this.base}/register`, {
      email,
      username,
      password,
      first_name: firstName,
      last_name: lastName,
    });
  }

  // ── onboarding wizard ─────────────────────────────────────────────────────
  checkOnboardingToken(token: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${this.base}/onboarding/check`, { token });
  }

  verifyEmail(token: string): Observable<PublicEndUser> {
    return this.http.post<PublicEndUser>(`${this.base}/verify-email`, { token });
  }

  saveProfile(token: string, firstName: string, lastName: string, phone: string): Observable<unknown> {
    return this.http.post(`${this.base}/onboarding/profile`, {
      token,
      first_name: firstName,
      last_name: lastName,
      phone,
    });
  }

  onboardingSendPhoneCode(token: string, phone: string): Observable<unknown> {
    return this.http.post(`${this.base}/onboarding/send-phone-code`, { token, phone });
  }

  onboardingVerifyPhone(token: string, code: string): Observable<unknown> {
    return this.http.post(`${this.base}/onboarding/verify-phone`, { token, code });
  }

  onboardingPattern(token: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/onboarding/pattern`, { token, pattern });
  }

  resendOnboarding(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/onboarding/resend`, { token });
  }

  // ── login ─────────────────────────────────────────────────────────────────
  // With 2FA off (the end-user default) signin returns status 'ok' with the
  // tokens directly — store the session here so the guard sees it. When 2FA is
  // enabled the tokens arrive later, from verify2fa; when the account is signed
  // in elsewhere, from confirmSignIn.
  signIn(username: string, password: string): Observable<EndUserSignInResponse> {
    return this.http
      .post<EndUserSignInResponse>(`${this.base}/signin`, { username, password, browser_id: browserId() })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  verifyPattern(username: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/pattern/verify`, { username, pattern });
  }

  verify2fa(challengeToken: string, code: string): Observable<EndUserAuthResponse> {
    return this.http
      .post<EndUserAuthResponse>(`${this.base}/2fa/verify`, { challenge_token: challengeToken, code })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  /** The user agreed to sign out their other browser or device: finish signing in. */
  confirmSignIn(confirmToken: string): Observable<EndUserAuthResponse> {
    return this.http
      .post<EndUserAuthResponse>(`${this.base}/signin/confirm`, { confirm_token: confirmToken })
      .pipe(tap((res) => this.storeIfSignedIn(res)));
  }

  resend2fa(challengeToken: string): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/resend`, { challenge_token: challengeToken });
  }

  // ── forgotten username ────────────────────────────────────────────────────
  //
  // Both ways back start from the address on the account and prove two things:
  // the phone registered on it, then the password. Only then is the username
  // emailed (recover) or replaced (reset) — see the dialog that drives this,
  // pages/consumer/end-user-forgot-username.

  /** Texts a code to the phone on that account. Always succeeds, whether or not the account exists. */
  sendUsernameCode(email: string): Observable<unknown> {
    return this.http.post(`${this.base}/username/send-code`, { email });
  }

  verifyUsernameCode(email: string, code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/username/verify-code`, { email, code });
  }

  verifyUsernamePassword(proofToken: string, password: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/username/verify-password`, {
      proof_token: proofToken,
      password,
    });
  }

  /** Emails the username to the address on the account. */
  sendUsername(proofToken: string): Observable<unknown> {
    return this.http.post(`${this.base}/username/send`, { proof_token: proofToken });
  }

  /** Sets a new username, for a user who cannot read that email. */
  resetUsername(proofToken: string, username: string): Observable<{ username: string }> {
    return this.http.post<{ username: string }>(`${this.base}/username/reset`, {
      proof_token: proofToken,
      username,
    });
  }

  // ── forgot security pattern ─────────────────────────────────────────────
  // Offered from the login page, so the account is named the way it was there.
  forgotPattern(username: string): Observable<unknown> {
    return this.http.post(`${this.base}/forgot-pattern`, { username });
  }

  resetPatternSendCode(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/reset-pattern/send-phone-code`, { token });
  }

  resetPatternVerifyPhone(token: string, code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/reset-pattern/verify-phone`, { token, code });
  }

  resetPattern(proofToken: string, pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/reset-pattern`, { proof_token: proofToken, pattern });
  }

  // ── passwords ─────────────────────────────────────────────────────────────
  // Named by username, like signing in; the link goes to the address on the
  // account.
  forgotPassword(username: string): Observable<unknown> {
    return this.http.post(`${this.base}/forgot-password`, { username });
  }

  // Reset-password verifies the registered phone first (send → verify → proof
  // → reset), matching admin/invited.
  resetPasswordSendCode(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/reset-password/send-phone-code`, { token });
  }

  resetPasswordVerifyPhone(token: string, code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/reset-password/verify-phone`, { token, code });
  }

  resetPassword(proofToken: string, newPassword: string): Observable<unknown> {
    return this.http.post(`${this.base}/reset-password`, { proof_token: proofToken, new_password: newPassword });
  }

  // ── self-service lock recovery ────────────────────────────────────────────
  recoverSendPhoneCode(token: string): Observable<unknown> {
    return this.http.post(`${this.base}/recover/send-phone-code`, { token });
  }

  recoverVerifyPhone(token: string, code: string): Observable<unknown> {
    return this.http.post(`${this.base}/recover/verify-phone`, { token, code });
  }

  // Recovery already verified the phone, so complete returns a password-reset
  // proof directly (used with resetPassword).
  recoverComplete(token: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(`${this.base}/recover/complete`, { token });
  }

  // ── authenticated (end-user token attached explicitly) ────────────────────
  me(): Observable<PublicEndUser> {
    return this.http
      .get<PublicEndUser>(`${this.base}/me`, { context: withSession('end_user') })
      .pipe(tap((u) => this.storeUser(u)));
  }

  changePassword(currentPassword: string, newPassword: string): Observable<unknown> {
    return this.http.post(
      `${this.base}/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { context: withSession('end_user') },
    );
  }

  // ── changing your email address or phone number ─────────────────────
  //
  // The same flows as the admin portal. Both take two codes, entered here on
  // the Security page — the first proves the request, the second applies it:
  //
  //   email: code -> the REGISTERED phone; then the new address, and a code to it
  //   phone: code -> the NEW phone;        then a code to the REGISTERED address
  //
  // The second code is entered on the Security page and applies the change.

  /** Email, step 1: texts a code to the REGISTERED phone — before any new address is asked for. */
  startEmailChange(): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${this.base}/me/email-change`,
      {},
      { context: withSession('end_user') },
    );
  }

  /** Email, step 2: verify that code. The new address is only asked for after this. */
  verifyEmailChangeCode(code: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${this.base}/me/email-change/verify-code`,
      { code },
      { context: withSession('end_user') },
    );
  }

  /** Email, step 3: the new address — a confirmation code is emailed to it. */
  submitNewEmail(newEmail: string): Observable<{ status: string; new_email: string }> {
    return this.http.post<{ status: string; new_email: string }>(
      `${this.base}/me/email-change/new-email`,
      { new_email: newEmail },
      { context: withSession('end_user') },
    );
  }

  /** Phone, step 1: texts a code to the NEW number. */
  startPhoneChange(newPhone: string): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${this.base}/me/phone-change`,
      { new_phone: newPhone },
      { context: withSession('end_user') },
    );
  }

  /** Phone, step 2: the code checks out, so a code goes to the REGISTERED address. */
  verifyPhoneChangeCode(code: string): Observable<{ status: string; email_hint: string }> {
    return this.http.post<{ status: string; email_hint: string }>(
      `${this.base}/me/phone-change/verify-code`,
      { code },
      { context: withSession('end_user') },
    );
  }

  /** Email, step 4: the code from the NEW address — applies the change. */
  confirmEmailChange(code: string): Observable<{ status: string; email: string }> {
    return this.http.post<{ status: string; email: string }>(
      `${this.base}/me/email-change/confirm`,
      { code },
      { context: withSession('end_user') },
    );
  }

  /** Phone, step 3: the code from the REGISTERED address — applies the change. */
  confirmPhoneChange(code: string): Observable<{ status: string; phone: string }> {
    return this.http.post<{ status: string; phone: string }>(
      `${this.base}/me/phone-change/confirm`,
      { code },
      { context: withSession('end_user') },
    );
  }

  // Set a FIRST password (social accounts, no current password): two steps —
  // send SMS code → verify phone (→ proof) → set password with the proof.
  setPasswordSendCode(): Observable<unknown> {
    return this.http.post(`${this.base}/set-password/send-phone-code`, {}, { context: withSession('end_user') });
  }

  setPasswordVerifyPhone(code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(
      `${this.base}/set-password/verify-phone`,
      { code },
      { context: withSession('end_user') },
    );
  }

  setPassword(proofToken: string, newPassword: string): Observable<unknown> {
    return this.http.post(
      `${this.base}/set-password`,
      { proof_token: proofToken, new_password: newPassword },
      { context: withSession('end_user') },
    );
  }

  /** Sets a FIRST pattern. Replacing one is phone-verified (below); a pattern can't be removed. */
  setSecurityPattern(pattern: string[]): Observable<unknown> {
    return this.http.post(`${this.base}/security-pattern`, { pattern }, { context: withSession('end_user') });
  }

  /** Pattern change, step 1: a code to the REGISTERED phone. */
  sendPatternCode(): Observable<{ status: string; phone_hint: string }> {
    return this.http.post<{ status: string; phone_hint: string }>(
      `${this.base}/security-pattern/send-phone-code`,
      {},
      { context: withSession('end_user') },
    );
  }

  /** Pattern change, step 2: the code checks out → a short-lived proof. */
  verifyPatternChangeCode(code: string): Observable<{ proof_token: string }> {
    return this.http.post<{ proof_token: string }>(
      `${this.base}/security-pattern/verify-phone`,
      { code },
      { context: withSession('end_user') },
    );
  }

  /** Pattern change, step 3: save the new pattern with that proof. */
  changeSecurityPattern(proofToken: string, pattern: string[]): Observable<unknown> {
    return this.http.post(
      `${this.base}/security-pattern/change`,
      { proof_token: proofToken, pattern },
      { context: withSession('end_user') },
    );
  }

  // ── 2FA method management ─────────────────────────────────────────────
  setupAuthenticator(currentPassword: string): Observable<TwoFactorSetup> {
    return this.http.post<TwoFactorSetup>(
      `${this.base}/2fa/setup`,
      { current_password: currentPassword },
      { context: withSession('end_user') },
    );
  }

  confirmAuthenticator(code: string): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/confirm`, { code }, { context: withSession('end_user') });
  }

  removeAuthenticator(currentPassword: string): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/remove`, { current_password: currentPassword }, { context: withSession('end_user') });
  }

  setDefaultMethod(method: 'email' | 'sms' | 'totp'): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/method`, { method }, { context: withSession('end_user') });
  }

  select2fa(challengeToken: string, method: 'email' | 'sms' | 'totp'): Observable<unknown> {
    return this.http.post(`${this.base}/2fa/select`, { challenge_token: challengeToken, method });
  }

  // ── social login (plugin-driven; empty list ⇒ no buttons) ─────────────────
  socialProviders(): Observable<string[]> {
    return this.http.get<{ providers: string[] }>(`${this.base}/social/providers`).pipe(
      map((r) => r.providers ?? []),
      catchError(() => of([])), // fail safe: no social if the endpoint is absent
    );
  }

  startSocial(provider: string): void {
    // Full-page navigation → gateway → Auth M → 302 to the provider.
    // The browser id rides along, so the session knows which browser signed in.
    window.location.href = `${this.base}/social/${provider}/start?browser_id=${encodeURIComponent(browserId())}`;
  }

  // The social callback page hands back the tokens; store them, then load /me.
  completeSocialLogin(tokens: TokenPair): Observable<PublicEndUser> {
    sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    this.startIdleWatch();
    this.socket.connect();
    return this.me();
  }

  // ── session ─────────────────────────────────────────────────────────────
  get tokens(): TokenPair | null {
    return readJson<TokenPair>(TOKENS_KEY);
  }

  get isLoggedIn(): boolean {
    return !!this.tokens;
  }

  logout(): void {
    this.endSession();
    this.router.navigate(['/']);
  }

  getLogoutEvent(): Observable<any> {
    return this.logoutEvent.asObservable();
  }

  /**
   * Single-flight: refresh tokens are single-use (rotated on the server), so
   * concurrent 401s must share ONE refresh — the same rule as the admin session.
   */
  refresh(): Observable<TokenPair> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }
    const refreshToken = this.tokens?.refresh_token ?? '';
    this.refreshInFlight$ = this.http
      .post<TokenPair>(
        `${GATEWAY_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        { context: withSession('end_user') },
      )
      .pipe(
        tap((pair) => sessionStorage.setItem(TOKENS_KEY, JSON.stringify(pair))),
        shareReplay({ bufferSize: 1, refCount: false }),
        finalize(() => (this.refreshInFlight$ = null)),
      );
    return this.refreshInFlight$;
  }

  /**
   * Called by the interceptor when the refresh token itself is dead (expired,
   * or revoked — e.g. by a password change): the session is over, so leave a
   * page that can no longer load anything and sign in again.
   */
  sessionExpired(): void {
    this.idle.stop();
    this.logoutEvent.next(true);
    this.clearSession();
    this.router.navigate(['/']);
  }

  /**
   * A sign-in from another browser or device replaced this session (heard on
   * the socket, or from a 401 "session_revoked"): end it here and say why.
   */
  sessionRevoked(): void {
    if (!this.isLoggedIn) return;
    this.idle.stop();
    this.clearSession();
    // Home, like any end-user sign-out; the header then opens the sign-in
    // dialog, saying why (end users have no sign-in page of their own).
    void this.router
      .navigate(['/'], { state: SIGNED_IN_ELSEWHERE_STATE })
      .finally(() => this.logoutEvent.next(SIGNED_IN_ELSEWHERE_REASON));
  }

  // ── session timeout ─────────────────────────────────────────────────────
  settings(): Observable<SessionSettings> {
    return this.http.get<SessionSettings>(`${this.base}/me/settings`, { context: withSession('end_user') });
  }

  saveSettings(settings: SessionSettings): Observable<SessionSettings> {
    return this.http.put<SessionSettings>(`${this.base}/me/settings`, settings, {
      context: withSession('end_user'),
    });
  }

  /** Apply a newly saved timeout to the running watch straight away. */
  applyTimeoutMinutes(minutes: number): void {
    if (this.isLoggedIn) {
      this.idle.watch(minutes);
    }
  }

  private startIdleWatch(): void {
    if (!this.isLoggedIn) return;
    this.settings().subscribe({
      next: (s) => this.isLoggedIn && this.idle.watch(s.session_timeout_minutes),
      error: () => this.isLoggedIn && this.idle.watch(DEFAULT_SESSION_TIMEOUT_MINUTES),
    });
  }

  /** The inactivity timeout elapsed: end the session like a sign-out, then log in again. */
  private timedOut(): void {
    this.endSession();
    this.router.navigate(['/']);
  }

  /** Revoke the refresh token server-side and forget the session locally. */
  private endSession(): void {
    this.idle.stop();
    const refreshToken = this.tokens?.refresh_token;
    if (refreshToken) {
      this.http
        .post(`${GATEWAY_URL}/auth/logout`, { refresh_token: refreshToken }, { context: withSession('end_user') })
        .subscribe({ error: () => undefined });
    }
    this.logoutEvent.next(true);
    this.clearSession();
  }

  private clearSession(): void {
    this.socket.disconnect();
    sessionStorage.removeItem(TOKENS_KEY);
    sessionStorage.removeItem(USER_KEY);
    this.user.set(null);
  }

  /** A session conflict (or a pending 2FA step) carries no tokens yet. */
  private storeIfSignedIn(res: { status: string; user?: PublicEndUser; tokens?: TokenPair }): void {
    if (res.status === 'ok' && res.user && res.tokens) {
      this.storeSession(res.user, res.tokens);
    }
  }

  private storeSession(user: PublicEndUser, tokens: TokenPair): void {
    sessionStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
    this.storeUser(user);
    this.startIdleWatch();
    this.socket.connect();
  }

  private storeUser(user: PublicEndUser): void {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    this.user.set(user);
  }
}

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
